import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import SecondaryNav from '@/nav/components/SecondaryNav';
import PrimaryNav from '@/nav/components/PrimaryNav';
import UserInfo from '@/nav/components/UserInfo/UserInfo';
import PortalLogo from '@/components/PortalLogo/PortalLogo';
import { ROUTE_NAMES } from '@/core/routes/routeNames';
import coreTokens from '@/core/tokens.json';

interface NavigationMenuProps {
  expanded?: boolean;
  setExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
}

function NavigationMenu({ expanded = true, setExpanded }: NavigationMenuProps) {
  const theme = useTheme();
  const { NAV_COLLAPSE, NAV_EXPAND, NAVIGATE_HOME } = coreTokens;

  const collapseButtonAction = setExpanded ? () => setExpanded((state) => !state) : () => false;

  return (
    <Box
      display="flex"
      width={expanded ? theme.nav.width.default : theme.nav.width.collapsed}
      flexShrink={0}
      height="100%"
      flexDirection="column"
      sx={{
        backgroundColor: 'custom.background',
        transitionDelay: '300ms',
        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        flexGrow="1"
        sx={{ overflowX: 'hidden', overflowY: 'auto' }}
      >
        <nav aria-label="primary" style={{ flex: 1 }}>
          <Box
            mt={4}
            mb={expanded ? 16.75 : 31.5}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Link
              component={ReactRouterLink}
              to={ROUTE_NAMES.ROOT}
              aria-label={NAVIGATE_HOME}
              title={NAVIGATE_HOME}
              color="text.primary"
              underline="none"
              display="flex"
            >
              <PortalLogo variant={expanded ? 'expanded' : 'collapsed'} />
            </Link>
          </Box>
          <Box className="nav-container" flex="1" sx={{ overflowX: 'hidden', overflowY: 'auto' }}>
            <PrimaryNav type={expanded ? 'expanded' : 'collapsed'} />
          </Box>
        </nav>
        <Box className="secondary-links">
          <Divider role="presentation" />
          <Box className="secondary-nav-contents" py={6}>
            <nav aria-label="secondary">
              <SecondaryNav type={expanded ? 'expanded' : 'collapsed'} />
            </nav>
          </Box>
          <Divider role="presentation" />
        </Box>
        <Box className="user-info">
          <UserInfo type={expanded ? 'expanded' : 'collapsed'} />
        </Box>
        <Box display="flex" justifyContent={expanded ? 'flex-end' : 'center'}>
          <IconButton
            id={expanded ? NAV_COLLAPSE : NAV_EXPAND}
            color="primary"
            aria-label={expanded ? NAV_COLLAPSE : NAV_EXPAND}
            onClick={collapseButtonAction}
            sx={{ p: 3 }}
            role="button"
          >
            {expanded ? (
              <NavigateBeforeIcon fontSize="large" titleAccess="Collapse Navigation Panel" />
            ) : (
              <NavigateNextIcon fontSize="large" titleAccess="Expand Navigation Panel" />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

export default NavigationMenu;
